/**
 * `/admin/auth/me` permissions → 사이드바용 메뉴 트리.
 * GET /admin/menus 대신 urlPath·menuCode로 라우트에 연결한다.
 * urlPath가 없으면 카탈로그 menuCode 경로로 폴백한다.
 */
import type { MenuPermission } from '@/entities/auth/api/auth.api';
import type { AdminMenuTreeNode } from '@/entities/menu/types';
import {
  CATALOG_PATH_BY_MENU_CODE,
  normalizeMenuUrlPath,
} from './menuUrlRouteMap';

const resolvePermissionUrlPath = (
  permission: MenuPermission,
): string | null => {
  const fromApi = normalizeMenuUrlPath(permission.urlPath);
  if (fromApi) return fromApi;
  return CATALOG_PATH_BY_MENU_CODE[permission.menuCode] ?? null;
};

/**
 * me.permissions를 상위→하위 트리로 조립한다.
 * visible=false는 사이드바에서 제외한다.
 */
export const buildMenuTreeFromPermissions = (
  permissions: MenuPermission[] | undefined | null,
): AdminMenuTreeNode[] | null => {
  if (!permissions?.length) return null;

  const nodes = new Map<string, AdminMenuTreeNode>();

  permissions.forEach((permission, index) => {
    if (permission.visible === false) return;
    if (!permission.menuCode) return;
    nodes.set(permission.menuCode, {
      id: permission.menuId ?? index + 1,
      menuKey: permission.menuCode,
      menuName: permission.menuName?.trim() || permission.menuCode,
      parentId: null,
      urlPath: resolvePermissionUrlPath(permission),
      displayOrder: permission.displayOrder ?? index,
      visible: true,
      accessScope: permission.accessScope ?? 'GENERAL',
      children: [],
    });
  });

  if (nodes.size === 0) return null;

  const attached = new Set<string>();
  for (const permission of permissions) {
    const node = nodes.get(permission.menuCode);
    if (!node) continue;
    const parentCode = permission.parentMenuCode;
    const isRoot = permission.topLevel || !parentCode;
    if (isRoot || !parentCode || !nodes.has(parentCode)) continue;
    const parent = nodes.get(parentCode);
    if (!parent) continue;
    node.parentId = parent.id;
    parent.children.push(node);
    attached.add(permission.menuCode);
  }

  const roots = [...nodes.values()].filter(
    (node) => !attached.has(node.menuKey),
  );
  roots.sort((a, b) => a.displayOrder - b.displayOrder);
  for (const root of roots) {
    root.children.sort((a, b) => a.displayOrder - b.displayOrder);
  }
  return roots;
};
